import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaFacebookF, FaYoutube, FaInstagram, FaCheckCircle } from 'react-icons/fa';
import { subscribe, clearSubscribeSuccess, clearError } from '../../Redux/slices/subscriberSlice';

const Footer = () => {
  const dispatch = useDispatch();
  const { loading, subscribeSuccess, error } = useSelector(state => state.subscribers);
  const [email, setEmail] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (email.trim()) {
      await dispatch(subscribe(email));
    }
  };

  useEffect(() => {
    if (subscribeSuccess) {
      setEmail('');
      const timer = setTimeout(() => {
        dispatch(clearSubscribeSuccess());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [subscribeSuccess, dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  return (
    <footer className="bg-white text-gray-800 py-12 border-t">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo and Description */}
          <div>
            <h2 className="font-serif italic text-2xl font-bold mb-4">House of Praise</h2>
            <p className="text-gray-600 mb-6 font-sf-pro">
              A spirit-filled community where healing flows, prophetic words are released, and God's supernatural power transforms lives through worship and ministry.
            </p>
            <div className="flex space-x-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-sm hover:bg-gray-800 transition" aria-label="Facebook">
                <FaFacebookF size={18} />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-sm hover:bg-gray-800 transition" aria-label="YouTube">
                <FaYoutube size={18} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-sm hover:bg-gray-800 transition" aria-label="Instagram">
                <FaInstagram size={18} />
              </a>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-4 font-sf-pro">Quick Links</h3>
            <ul className="space-y-2 font-sf-pro">
              <li><Link to="/" className="text-gray-600 hover:text-gray-900 transition">Home</Link></li>
              <li><Link to="/about" className="text-gray-600 hover:text-gray-900 transition">About us</Link></li>
              <li><Link to="/sermon" className="text-gray-600 hover:text-gray-900 transition">Sermon</Link></li>
              <li><Link to="/ministries" className="text-gray-600 hover:text-gray-900 transition">Ministries</Link></li>
              <li><Link to="/contact" className="text-gray-600 hover:text-gray-900 transition">Contact us</Link></li>
              <li><Link to="/events" className="text-gray-600 hover:text-gray-900 transition">Events</Link></li>
              <li><Link to="/giving" className="text-gray-600 hover:text-gray-900 transition">Give</Link></li>
            </ul>
          </div>
          
          {/* Service Times */}
          <div>
            <h3 className="text-xl font-bold mb-4 font-sf-pro">Service Times</h3>
            <ul className="space-y-2 font-sf-pro">
              <li className="text-gray-600">Sunday Morning 8:00 AM</li>
              <li className="text-gray-600">Wednesday Evening 5:00 PM</li>
              <li className="text-gray-600">Small Groups: Various Times</li>
            </ul>
          </div>
          
          {/* Newsletter */}
          <div>
            <h3 className="text-xl font-bold mb-4 font-sf-pro">Newsletter</h3>
            <p className="text-gray-600 mb-4 font-sf-pro">
              Subscribe to receive updates and news
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-sf-pro"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 font-sf-pro disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </button>
              {subscribeSuccess && (
                <div className="flex items-center gap-2 text-green-600 text-sm">
                  <FaCheckCircle />
                  <span>Subscribed successfully!</span>
                </div>
              )}
              {error && (
                <p className="text-red-600 text-sm">{error}</p>
              )}
            </form>
          </div>
          
          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-4 font-sf-pro">Contact Info</h3>
            <ul className="space-y-3 font-sf-pro">
              <li className="flex items-start">
                <span className="inline-block mr-2 mt-1">📍</span>
                <span className="text-gray-600">
                  No 10 Sam Adewoye Idi mango,<br />
                  Aro lambo inside Matogun Road<br />
                  Oke-Aro Ogun State
                </span>
              </li>
              <li className="flex items-center">
                <span className="inline-block mr-2">✉️</span>
                <a href="mailto:houseofpraise55@gmail.com" className="text-gray-600 hover:text-gray-900 transition">
                  houseofpraise55@gmail.com
                </a>
              </li>
              <li className="flex items-center">
                <span className="inline-block mr-2">📞</span>
                <a href="tel:+2349131920473" className="text-gray-600 hover:text-gray-900 transition">
                  +234-9131920473
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-gray-200 mt-12 pt-8 pb-4 text-center text-gray-600">
          <p className="font-sf-pro">
            © 2025 House of Praise. All rights reserved | <Link to="/admin/login" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition">Privacy Policy</Link> | <Link to="/terms" className="hover:text-gray-900 transition">Terms of Service</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;